import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Stack,
  Box,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import dayjs from 'dayjs';
import {
  formatAmount,
  getTypeLabel,
  getTypeColor,
  CashDocumentType,
} from '../../Types/cashDocumentTypes';
import type { CashDocumentCardProps } from './CashDocumentCard.types';

/**
 * Cash Document Card Component
 *
 * Displays a single cash document (KP/KW) in a card format
 * Used in reservation details page to show linked cash documents
 */
const CashDocumentCard: React.FC<CashDocumentCardProps> = ({
  document,
  onEdit,
  onDelete,
  onDownloadPdf,
  compact = false,
  isDeleteConfirm = false,
}) => {
  return (
    <Card
      sx={{
        border: '2px solid',
        borderColor: isDeleteConfirm ? 'error.main' : 'divider',
        backgroundColor: isDeleteConfirm ? 'error.lighter' : 'background.paper',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        <Stack spacing={1.5}>
          {/* Header: Type Chip + Amount */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip
              label={getTypeLabel(document.type)}
              color={getTypeColor(document.type)}
              size="small"
            />
            <Typography
              variant={compact ? 'body1' : 'h6'}
              fontWeight="bold"
              color={document.type === CashDocumentType.KP ? 'success.main' : 'error.main'}
            >
              {formatAmount(document.amount, document.currency)}
            </Typography>
          </Box>

          {/* Document Number + Date */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              {document.documentNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {document.documentDate ? dayjs(document.documentDate).format('DD.MM.YYYY') : '-'}
            </Typography>
          </Box>

          {/* Person */}
          {!compact && (
            <Typography variant="body2" color="text.secondary">
              {document.personName}
            </Typography>
          )}

          {/* Purpose (if exists) */}
          {!compact && document.purpose && (
            <Typography variant="body2" noWrap>
              {document.purpose}
            </Typography>
          )}

          {/* Actions */}
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {onDownloadPdf && (
              <Tooltip title="Pobierz PDF">
                <IconButton size="small" onClick={() => onDownloadPdf(document.id)} color="primary">
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edytuj">
                <IconButton size="small" onClick={() => onEdit(document.id)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={isDeleteConfirm ? '⚠️ Kliknij ponownie aby potwierdzić' : 'Usuń'}>
                <IconButton
                  size="small"
                  onClick={() => onDelete(document.id)}
                  color="error"
                  sx={{
                    animation: isDeleteConfirm ? 'pulse 1s infinite' : 'none',
                    backgroundColor: isDeleteConfirm ? 'error.main' : 'transparent',
                    color: isDeleteConfirm ? 'error.contrastText' : 'error.main',
                    '&:hover': {
                      backgroundColor: isDeleteConfirm ? 'error.dark' : 'error.lighter',
                    },
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.15)' },
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CashDocumentCard;
